import {useEffect, useState} from "react";
import {Alert, Box, Container, Grid, Skeleton, Snackbar, styled, Typography,} from "@mui/material";
import {Helmet} from "react-helmet";
import {DownloadRounded, ErrorRounded} from "@mui/icons-material";
import {useParams, useSearchParams} from "react-router-dom";
import ConfirmDialog from "../../components/common/confirmDialog";
import TransferModal from "../../components/dashboard/drive/transferModal";
import {addTag, copyFiles, createFolder, deleteFiles, deleteFolder, downloadBlob, downloadFiles, getFiles, getFolders, moveFiles, moveFolders, removeTag, renameFolder, searchInDrive} from "../../lib/services/drive";
import Toasty from "../../theme/components/Toasty";
import File, {ItemContainer} from "../../components/dashboard/drive/file";
import InputFieldModal from "../../components/dashboard/drive/inputFieldModal";
import UploadFileModal from "../../components/dashboard/drive/uploadFileModal";
import Toolbar from "../../components/dashboard/drive/toolbar";
import ShareFileModal from "../../components/home/upload/shareFileModal";
import ProgressButton from "../../components/common/progressButton";

export default function Dashboard() {

    // Initial Props
    const {folderId} = useParams();
    const [folders, setFolders] = useState([]);
    const [files, setFiles] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [multipleSelect, setMultipleSelect] = useState(false);
    const [hasUploaded, setHasUploaded] = useState(false);
    const [pending, setPending] = useState(false);
    const [loading, setLoading] = useState(true);

    // Modals
    const [transferType, setTransferType] = useState('');
    const [transferModalOpen, setTransferModalOpen] = useState(false);
    const [uploadFileModalOpen, setUploadFileModalOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [createFolderModal, setCreateFolderModal] = useState(false);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareFolderModalOpen, setShareFolderModalOpen] = useState(false);
    const [addTagModal, setAddTagModal] = useState(false);

    const isSelected = (id) => Boolean(selectedItems?.find(i => i.id === id));
    const [openedDrawer, setOpenedDrawer] = useState(false);

    const [searchParams] = useSearchParams();
    const searchQuery = searchParams.get('search');

    // Download
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [downloadCompleted, setDownloadCompleted] = useState(false);
    const [downloadFailed, setDownloadFailed] = useState(false);
    const [downloadController, setDownloadController] = useState(new AbortController());

    const [openSnackBar, setOpenSnackBar] = useState(false);

    const handleOpenSnackBar = () => {
        setOpenSnackBar(true);
    }
    const handleCloseSnackBar = () => {
        setOpenSnackBar(false);
    }
    const handleSelect = (e, file) => {
        // console.log(e?.force)
        const allFoldersAndFiles = [].concat(files, folders);
        let newSelectedFiles = [...selectedItems];
        const isSelectedBefore = Boolean(selectedItems.find(i => i.id === file.id));
        if (isSelectedBefore && !e?.force) {
            return setSelectedItems(newSelectedFiles.filter(i => i.id !== file.id));
        }
        const selectedItem = allFoldersAndFiles.find(i => i.id === file.id);
        if (multipleSelect || e.ctrlKey) {
            newSelectedFiles.push(selectedItem);
        } else {
            newSelectedFiles = [file];
        }
        setSelectedItems(newSelectedFiles);
    }

    // Folders
    const handleCreateFolder = async (name) => {
        setPending(true);
        try {
            const data = await createFolder(folderId, name);
            if (data?.isSuccess) {
                const newFolders = [...folders];
                newFolders.push({
                    id: data?.value,
                    type: "folder",
                    name,
                })
                setFolders(newFolders);
                if (data.successMessage) Toasty.success(data.successMessage);
                setCreateFolderModal(false);
            } else {
                if (data && data.errors?.length) {
                    data.errors.map(i => Toasty.error(i));
                }
            }
        } catch (err) {
            Toasty.error('خاطیی غیر منتظره رخ داد')
        }
        setPending(false);
    }
    const handleDeleteFolder = async (folderId, options = {}) => {
        if (!folderId) return;
        const {noSuccessMessage, noUpdate} = options
        try {
            setPending(true);
            const data = await deleteFolder(folderId);
            if (data?.isSuccess) {
                if (!noUpdate) {
                    const newFolders = [...folders];
                    setFolders(newFolders.filter(i => i.id !== folderId));
                }
                if (data.successMessage && !noSuccessMessage) Toasty.success(data.successMessage);
            } else {
                if (data && data.errors?.length) {
                    data.errors.map(i => Toasty.error(i));
                    setPending(false);
                    return Promise.reject('خاطیی غیر منتظره رخ داد');
                }
            }
        } catch (err) {
            Toasty.error('خاطیی غیر منتظره رخ داد');
            setPending(false);
            return Promise.reject('خاطیی غیر منتظره رخ داد');
        }
        setPending(false);

    }
    const handleRenameFolder = async (name) => {
        setPending(true);
        try {
            const data = await renameFolder(selectedItem.id, name);
            if (data?.isSuccess) {
                const newFolders = [...folders];
                const selected = newFolders.find(i => i.id === selectedItem.id);
                selected.name = name;
                setFolders(newFolders);
                if (data.successMessage) Toasty.success(data.successMessage);
            } else {
                if (data && data.errors?.length) {
                    data.errors.map(i => Toasty.error(i));
                }
            }
        } catch (err) {
            Toasty.error('خاطیی غیر منتظره رخ داد')
        }
        setPending(false);
    }
    const handleMoveFolders = async(foldersId, parentId) => {
        if (parentId === folderId) return Toasty.error('لطفا پوشه مقصد را به صورت صحیح وارد کنید');
        setPending(true);
        try {
            const data = await moveFolders(foldersId, parentId);
            if (data?.isSuccess) {
                let newFolders = [...folders];
                foldersId.forEach(i => {
                    const index = newFolders.findIndex(f => f.id === i);
                    if (index >= 0) newFolders.splice(index, 1);
                });
                setFolders(newFolders);
                setSelectedItem(null);
                if (data.successMessage) Toasty.success(data.successMessage);
            } else {
                if (data && data.errors?.length) {
                    data.errors.map(i => Toasty.error(i));
                }
            }
        }
        catch (err) {
            console.log(err);
            Toasty.error('خاطیی غیر منتظره رخ داد')
        }
        setPending(false);
    }
    const handleDownloadFolder = async(folderId, name) => {
        const newFolders = [...folders];
        const selected = newFolders.findIndex(f => f.id === folderId);
        if (selected < 0) return;
        setPending('downloadingZip' + folderId);
        const folder = newFolders[selected];
        try {
            const filesData = await getFiles(folderId);
            if (!filesData.length) {
                Toasty.error('این پوشه خالی است');
                return setPending(false);
            }
            folder.downloadProgress = 50;
            folder.downloadCompleted = false;
            folder.downloadFailed = false;
            folder.controller = new AbortController();
            const fileIds = filesData.map(f => f.id);
            const {data} = await downloadFiles('Drive', {folderId, fileIds, isZip: true}, {onProgress: (progress) => {const updatedFolders = [...folders]; updatedFolders[selected].downloadProgress = progress; return setFolders(updatedFolders)}, signal: folder?.controller?.signal});
            const zippedName = name + ".zip";
            await downloadBlob(data, zippedName, true);
            folder.downloadCompleted = true;
            folder.downloadProgress = 0;
        } catch (err) {
            if (folder?.controller?.signal?.reason === "user canceled") {
                folder.controller = new AbortController();
                return setFolders(newFolders);
            }
            folder.downloadFailed = true;
            console.log(err);
            Toasty.error('خطایی غیر منتظره رخ داد')
        }
        setFolders(newFolders);
        setPending(false);
    }

    // Files
    const handleMoveFiles = async(ids, parentId) => {
        if (!ids.length) return;
        if (parentId === folderId) return Toasty.error('لطفا پوشه مقصد را به صورت صحیح وارد کنید');
        setPending(true);
        try {
            const data = await moveFiles(ids, parentId);
            if (data?.isSuccess) {
                let newFiles = [...files];
                ids.forEach(id => {
                    newFiles = newFiles.filter(i => i.id !== id);
                })
                setFiles(newFiles);
                if (data.successMessage) Toasty.success(data.successMessage);
                setTransferModalOpen(false);
            } else {
                if (data && data.errors?.length) {
                    data.errors.map(i => Toasty.error(i));
                }
            }
        } catch (err) {
            Toasty.error('خاطیی غیر منتظره رخ داد')
        }
        setPending(false);
    }
    const handleCopyFiles = async(folderId) => {
        const ids = selectedItems.map(i => i.id);
        if (!ids.length) return;
        setPending(true);
        try {
            const data = await copyFiles(ids, folderId);
            if (data?.isSuccess) {
                if (data.successMessage) Toasty.success(data.successMessage);
                setTransferModalOpen(false);
            } else {
                if (data && data.errors?.length) {
                    data.errors.map(i => Toasty.error(i));
                }
            }
        }
        catch (err) {
            Toasty.error('خاطیی غیر منتظره رخ داد')
        }
        setPending(false);
    }
    const handleDeleteFiles = async(ids, options) => {
        if (!ids.length) return;
        setPending(true);
        try {
            const data = await deleteFiles(ids);
            if (data?.isSuccess) {
                let newFiles = [...files];
                ids.forEach(id => {
                    newFiles = newFiles.filter(i => i.id !== id);
                })
                setFiles(newFiles);
                if (data.successMessage) Toasty.success(data.successMessage);
                setDeleteConfirmOpen(false);
            } else {
                if (data && data.errors?.length) {
                    data.errors.map(i => Toasty.error(i));
                }
            }
        } catch (err) {
            Toasty.error('خاطیی غیر منتظره رخ داد')
        }
        setPending(false);
    }
    const handleDownloadFiles = async(fileIds, name, isZip, hasSnackBar=false) => {
        if (!fileIds.length) return;
        if (fileIds.length === 1) {
            setPending((isZip ? 'downloadingZip' : 'downloading') + fileIds[0]);
        } else setPending(isZip ? 'downloadingZip' : 'downloading');
        try {
            setDownloadFailed(false);
            setDownloadCompleted(false);
            setDownloadProgress(0);
            if (hasSnackBar) handleOpenSnackBar();
            const {data} = await downloadFiles('Drive', {fileIds, isZip}, {onProgress: (progress) => setDownloadProgress(progress), signal: downloadController.signal});
            if (isZip) {
                const extension = name.split('.').pop();
                if (!extension) return;
                const zippedName = name.replace(`.${extension}`, '.zip');
                await downloadBlob(data, zippedName, isZip);
            } else {
                await downloadBlob(data, name, isZip);
            }
        } catch (err) {
            if (downloadController?.signal?.reason === "user canceled") {
                return setDownloadController(new AbortController());
            }
            setDownloadFailed(true);
            handleCloseSnackBar()
            console.log(err);
            Toasty.error('خطایی غیر منتظره رخ داد');
        }
        handleCloseSnackBar()
        setPending(false);
    }
    const handleAddTag = async (value) => {
        const lastSelected = [...selectedItems].pop();
        const selected = selectedItem || lastSelected;
        if (!selected) return;
        if (selected.tags?.length) {
            const duplicateIndex = selected.tags.findIndex(t => t.name === value);
            if (duplicateIndex >= 0) return Toasty.error(`یک تگ با نام ${value} وجود دارد`);
        }
        setPending(true);
        try {
            const {data} = await addTag(selected.id, value);
            if (data?.isSuccess) {
                const {tagId} = data.value;
                const newTag = {
                    id: tagId,
                    name: value
                }
                const newFiles = [...files];
                    const tags = selected.tags?.length ? [...selected.tags] : [];
                    const selectedIndex = newFiles.findIndex(i => i.id === selected.id);
                    tags.push(newTag);
                    newFiles[selectedIndex].tags = tags;
                    setSelectedItem({...selected, tags});
                    setFiles(newFiles);
                if (data.successMessage) Toasty.success(data.successMessage);
                setAddTagModal(false);
            } else {
                if (data && data.errors?.length) {
                    data.errors.map(i => Toasty.error(i));
                }
            }
        } catch (err) {
            console.log(err);
            Toasty.error('خطایی غیر منتظره رخ داد')
        }
        setPending(false);
    }
    const handleRemoveTag = async(fileId, tagName) => {
        try {
            const {data} = await removeTag(fileId, tagName);
            if (data.isSuccess) {
                const newFiles = [...files];
                const tags = selectedItem.tags?.filter(t => t.name !== tagName);
                const selectedIndex = newFiles.findIndex(i => i.id === selectedItem.id);
                if (selectedIndex >= 0) {
                newFiles[selectedIndex].tags = tags;
                setSelectedItem({...selectedItem, tags});
                }
                setFiles(newFiles);
                if (data.successMessage) Toasty.success(data.successMessage);
            } else {
                if (data && data.errors?.length) {
                    data.errors.map(i => Toasty.error(i));
                }
            }
        } catch (err) {
            console.log(err);
            Toasty.error('خطایی غیر منتظره رخ داد')
        }
    }

    // Both
    const handleMoveItems = (parentId) => {
        if (!selectedItems.length) return;
        const folders = selectedItems.filter(i => i.type === 'folder');
        const files = selectedItems.filter(i => i.type !== 'folder');
        if (folders.length) handleMoveFolders(folders.map(i => i.id), parentId);
        if (files.length) handleMoveFiles(files.map(i => i.id), parentId);
    }
    const handleDeleteItems = async () => {
        if (!selectedItems.length) return;
        const selectedFolders = selectedItems.filter(i => i.type === 'folder');
        const selectedFiles = selectedItems.filter(i => i.type !== 'folder');
        if (selectedFolders.length) {
            try {
                let isError = false;
                let newFolders = [...folders];
                selectedFolders.forEach(folder => {
                    newFolders = newFolders.filter(f => f.id !== folder.id);
                });
                setFolders(newFolders);
                for await (const folder of selectedFolders) {
                    try {
                        await handleDeleteFolder(folder.id, {noSuccessMessage: true, noUpdate: true});
                    } catch (err) {
                        newFolders.push(folder);
                        setFolders(newFolders);
                        isError = true;
                    }
                }
                setDeleteConfirmOpen(false);
                if (isError) return;
                Toasty.success('فولدر های انتخاب شده با موفقیت پاک شدند')
            } catch (err) {
                Toasty.error('خطایی غیر منتظره رخ داد')
            }
        }
        if (selectedFiles.length) handleDeleteFiles(selectedFiles.map(i => i.id));
    }
    const handleCancelDownload = (fileId, fileType) => {
        switch (fileType) {
            case "folder": {
                const selected = folders.findIndex(f => f.id === fileId);
                if (selected < 0) return;
                folders[selected]?.controller?.abort('user canceled');
                const newFolders = [...folders];
                newFolders[selected].downloadCompleted = false;
                newFolders[selected].downloadProgress = 0;
                setPending(false);
                break;
            }
            default: {
                downloadController.abort('user canceled');
                setDownloadFailed(false);
                setDownloadCompleted(false);
                setDownloadProgress(0);
                setPending(false);
            }
        }
        // selectedFile.controller.abort('user canceled');
        // const newFile = {...selectedFile}
        // newFile.downloadCompleted = false;
        // newFile.downloadProgress = 0;
        // setSelectedFile(newFile);
        // setPending(false);
    }

    // Initialize Data
    async function getAllData() {
        setLoading(true);
        const foldersPayload = await getFolders(folderId);
        setFolders(foldersPayload);
        const filesPayload = await getFiles(folderId);
        setFiles(filesPayload);
        setLoading(false);
    }
    useEffect(() => {
        if (!searchQuery) getAllData();
        setSelectedItems([]);
        setHasUploaded(false);
    }, [folderId, hasUploaded, searchQuery]);
    // Get Search Data
    async function getSearchData(query) {
        setLoading(true);
        try {
            const {folders, files} = await searchInDrive(query);
            setFolders(folders);
            setFiles(files);
        } catch (err) {
            console.log(err);
            Toasty.error('خطایی غیر منتظره رخ داد')
        }
        setLoading(false);
    }
    useEffect(() => {
        if (searchQuery) {
            getSearchData(searchQuery);
        }
    }, [searchQuery]);

    return (
        <>
            <style jsx global>{`
                  html,body,#root {
                    max-height: 100%;
                    overflow: hidden;
                  }
            `}</style>
            <Helmet>
                <title>درایو میلفا</title>
            </Helmet>
            <Container sx={{ mt: 3 }}>
                <Typography fontSize={{ xs: "1.1rem" }} fontWeight={600}>مدیریت فایل ها</Typography>
                {/* Information & Actions */}
                <Toolbar
                    mt={1.5}
                    folders={folders}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    setUploadFileModalOpen={setUploadFileModalOpen}
                    setCreateFolderModal={setCreateFolderModal}
                    setTransferType={setTransferType}
                    setTransferModalOpen={setTransferModalOpen}
                    multipleSelect={multipleSelect}
                    setMultipleSelect={setMultipleSelect}
                    setDeleteConfirmOpen={setDeleteConfirmOpen}
                    handleDownloadFiles={handleDownloadFiles}
                    handleDownloadFolder={handleDownloadFolder}
                    setShareModalOpen={setShareModalOpen}
                    setShareFolderModalOpen={setShareFolderModalOpen}
                    pending={pending}
                    setAddTagModal={setAddTagModal}/>
                {loading
                    ?
                    <LoadingSkeleton/>
                    :
                    <Box component="article" sx={{ maxHeight: {xs: "70vh", xxl: "75vh"}, pb: {xs: "35vh", lg: "25vh"}, overflowY: "auto" }}>
                        {/* Folders */}
                        <Section sx={{ mt: 3 }}>
                            <Typography fontSize={{ lg: "1.1rem" }} fontWeight={500}>پوشه ها</Typography>
                            {folders.length
                                ?
                                <Grid mt={0} container spacing={2}>
                                    {folders.map((f, index) => (
                                        <Grid key={index} item xs={6} md={4} lg={3} xl={2}>
                                            <File
                                                onClick={(e) => handleSelect(e,f)}
                                                handleSelect={(e) => handleSelect(e,f)}
                                                selected={isSelected(f.id)}
                                                file={f} pending={pending}
                                                onDelete={handleDeleteFolder}
                                                onRename={handleRenameFolder}
                                                onMoved={handleMoveFolders}
                                                onShare={() => setShareFolderModalOpen(true)}
                                                onDownloadZip={() => handleDownloadFolder(selectedItem.id, selectedItem.name, true)}
                                                onDownloadCancel={handleCancelDownload}
                                                setSelectedItem={setSelectedItem}/>
                                        </Grid>
                                    ))}
                                </Grid>
                                :
                                <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                                    <ErrorRounded color="warning" fontSize="large"/>
                                    <Typography mt={.5}>پوشه ای یافت نشد!</Typography>
                                </Box>
                            }
                        </Section>
                        {/* Files */}
                        <Section sx={{ mt: 3 }}>
                            <Typography fontSize={{ lg: "1.1rem" }} fontWeight={500}>فایل ها</Typography>
                            {files.length
                                ?
                                <Grid mt={0} container spacing={2}>
                                    {files.map((f, index) => (
                                        <Grid key={index} item xs={6} md={4} lg={3} xl={2}>
                                            <File
                                                file={f}
                                                files={files}
                                                onClick={(e) => handleSelect(e,f)}
                                                handleSelect={(e) => handleSelect(e,f)}
                                                setSelectedItem={setSelectedItem} pending={pending}
                                                onDelete={() => handleDeleteFiles([selectedItem.id])}
                                                onDownload={() => handleDownloadFiles([selectedItem.id], selectedItem.name)}
                                                onDownloadZip={() => handleDownloadFiles([selectedItem.id], selectedItem.name, true)}
                                                onDownloadCancel={handleCancelDownload}
                                                downloadProgress={downloadProgress}
                                                downloadFailed={downloadFailed}
                                                downloadController={downloadController}
                                                onShare={() => setShareModalOpen(true)}
                                                onAddTag={() => setAddTagModal(true)}
                                                onRemoveTag={handleRemoveTag}
                                                selected={isSelected(f.id)}
                                                openedDrawer={openedDrawer}
                                                setOpenedDrawer={setOpenedDrawer}/>
                                        </Grid>
                                    ))}
                                </Grid>
                                :
                                <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                                    <ErrorRounded color="warning" fontSize="large"/>
                                    <Typography mt={.5}>فایلی یافت نشد!</Typography>
                                </Box>
                            }
                        </Section>
                    </Box>
                }
            </Container>

            {/* Transfer Modal */}
            <TransferModal
                open={transferModalOpen}
                onClose={() => setTransferModalOpen(false)}
                foldersId={selectedItems?.filter(i => i.type === 'folder')?.map(i => i.id)}
                onConfirm={transferType === 'move' ? handleMoveItems : handleCopyFiles}
                pending={pending}/>

            {/* Upload File Modal */}
            <UploadFileModal
                open={uploadFileModalOpen}
                onClose={() => setUploadFileModalOpen(false)}
                setHasUploaded={setHasUploaded}/>

            {/* Delete Confirm Dialog */}
            <ConfirmDialog
                open={deleteConfirmOpen}
                title="آیا مطمئنی میخوای حذف کنی؟!"
                description="اطلاعات پاک شده قابل بازیابی نیست"
                color="error" pending={pending}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={() => handleDeleteItems(selectedItems)}/>

            {/* Create Folder Modal */}
            <InputFieldModal
                open={createFolderModal}
                onClose={() => setCreateFolderModal(false)}
                onConfirm={handleCreateFolder}
                title="ایجاد پوشه جدید"
                inputProps={{ label: "نام پوشه", placeholder: "نام پوشه را وارد کنید" }}
                pending={pending}/>

            {/* Share File / reUpload */}
            <ShareFileModal
                open={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                shareType='file'
                files={selectedItems}
                setFiles={setSelectedItems}/>

            {/* Share Folder */}
            <ShareFileModal
                open={shareFolderModalOpen}
                onClose={() => setShareFolderModalOpen(false)}
                shareType='folder'
                folderId={selectedItem?.id || [...selectedItems]?.pop()?.id}/>

            {/* Add Tag */}
            <InputFieldModal
                open={addTagModal}
                onClose={() => setAddTagModal(false)}
                onConfirm={handleAddTag}
                title="افزودن تگ جدید"
                inputProps={{ label: "نام تگ", placeholder: "نام تگ را وارد کنید" }}
                options={{ clearAfter: true }}
                pending={pending}/>

            {/* SnackBar */}
            <Snackbar open={openSnackBar} message={
                <ProgressButton
                    downloading={true}
                    isCompleted={downloadCompleted}
                    onClick={() => {}}
                    onCancel={handleCancelDownload}

                    progress={downloadProgress}
                    isFailed={downloadFailed}
                    variant="contained"
                    color="success" fullWidth
                    startIcon={<DownloadRounded/>}
                    sx={{ py: 1.5, width: 1 }} disableElevation>دانلود</ProgressButton>
            }/>
                {/*<Alert severity="success" sx={{ width: '100%' }}>*/}


                {/*    <ProgressButton*/}
                {/*        downloading={true}*/}
                {/*        isCompleted={downloadCompleted}*/}
                {/*        onClick={() => {}}*/}
                {/*        onCancel={handleCancelDownload}*/}

                {/*        progress={downloadProgress}*/}
                {/*        isFailed={downloadFailed}*/}
                {/*        variant="contained"*/}
                {/*        color="success" fullWidth*/}
                {/*        startIcon={<DownloadRounded/>}*/}
                {/*        sx={{ py: 1.5 }} disableElevation>دانلود</ProgressButton>*/}

                {/*</Alert>*/}


            {/*</Snackbar>*/}
        </>
    )
}

const Section = styled('section')(({theme}) => ({
    background: theme.palette.mode === 'light' ? "#f6f9fb" : theme.palette.background.box,
    padding: 16,
    border: `1px solid ${theme.palette.grey.light}`,
    borderRadius: 8,
    [theme.breakpoints.up('lg')]: {padding: 24}
}))
const LoadingSkeleton = () => {
    return (
        <>
            <Section sx={{ mt: 3 }}>
                <Typography fontSize={{ lg: "1.1rem" }} fontWeight={500}>پوشه ها</Typography>
                    <Grid mt={0} container spacing={2}>
                        {Array.from([...Array(3)]).map((_, index) => (
                            <Grid key={index} item xs={6} md={4} lg={3} xl={2}>
                                <ItemContainer sx={{ cursor: "initial" }}>
                                    <Skeleton variant="rounded" width="100%" height="100%" sx={{ borderRadius: "inherit" }}/>
                                </ItemContainer>
                            </Grid>
                        ))}
                    </Grid>
            </Section>
            <Section sx={{ mt: 3 }}>
                <Typography fontSize={{ lg: "1.1rem" }} fontWeight={500}>فایل ها</Typography>
                <Grid mt={0} container spacing={2}>
                    {Array.from([...Array(3)]).map((_, index) => (
                        <Grid key={index} item xs={6} md={4} lg={3} xl={2}>
                            <ItemContainer sx={{ cursor: "initial" }}>
                                <Skeleton variant="rounded" width="100%" height="100%" sx={{ borderRadius: "inherit" }}/>
                            </ItemContainer>
                        </Grid>
                    ))}
                </Grid>
            </Section>
        </>
    )
}
